import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { StoreProductListItem } from '@nucleus-commerce/api-client/admin';

import { storeCatalogApi } from '../api/store-catalog-api';

import { storeCatalogKeys } from './store-catalog-keys';

/** A rotation that will reassert the status of some of the rows at its next run. */
export interface RotationOwner {
  id: string;
  name: string;
  /** Null while paused: the next run is not known. */
  nextRunAt: string | null;
  /** How many of the rows it owns; null when the rows are not loaded ("all matching"). */
  count: number | null;
}

/** A rotation that runs (or will again once resumed) reverts manual changes; draft and ended do not. */
export const REASSERTS: ReadonlySet<string> = new Set(['active', 'paused']);

/**
 * The rotations that own some of these store products and would revert a manual status change
 * at their next run (D-181, decision 11), one per rotation with how many rows it owns. Each
 * rotation is read once (cached briefly); one that cannot be read counts as owning, so the
 * warning errs on the side of showing.
 */
export function useRotationOwners() {
  const queryClient = useQueryClient();
  return useCallback(
    async (rows: ReadonlyArray<Pick<StoreProductListItem, 'schedule'>>) => {
      const counts = new Map<string, { name: string; count: number }>();
      for (const { schedule } of rows) {
        if (!schedule) continue;
        const seen = counts.get(schedule.id);
        counts.set(schedule.id, { name: schedule.name, count: (seen?.count ?? 0) + 1 });
      }
      const owners = await Promise.all(
        [...counts].map(async ([id, { name, count }]): Promise<RotationOwner | null> => {
          try {
            const schedule = await queryClient.query({
              queryKey: storeCatalogKeys.scheduleOption(id),
              queryFn: () => storeCatalogApi.schedule(id),
              staleTime: 30_000,
            });
            if (!REASSERTS.has(schedule.status)) return null;
            return { id, name: schedule.name, nextRunAt: schedule.nextRunAt, count };
          } catch {
            return { id, name, nextRunAt: null, count };
          }
        }),
      );
      return owners.filter((owner) => owner !== null);
    },
    [queryClient],
  );
}
